import { skipToken, useQuery } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { GetJobLogPresignedUrlDocument } from "~/components/job-log/gqls";
import { useTenant } from "~/features/tenant/context";
import type { JobRun, JobRunLogResult, LogMessage } from "./types";

async function decompressBlob(blob: Blob) {
  const ds = new DecompressionStream("gzip");
  const decompressedStream = blob.stream().pipeThrough(ds);
  return new Response(decompressedStream).blob();
}

export const useJobRunS3Log = (
  { jobRunId, jobName, s3LogPath }: JobRun,
  { enabled }: { enabled: boolean },
): JobRunLogResult => {
  const logSource = `s3: ${s3LogPath}`;

  // Local state only for the S3 logs (external system)
  const [logs, setLogs] = useState<LogMessage[]>([]);
  const [logsError, setLogsError] = useState<Error | null>(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const { tenantId } = useTenant();

  const {
    data,
    error: queryError,
    loading: queryLoading,
  } = useQuery(
    GetJobLogPresignedUrlDocument,
    enabled && tenantId
      ? {
          variables: {
            input: {
              tenantId,
              jobRunId,
              jobName,
            },
          },
          fetchPolicy: "no-cache",
        }
      : skipToken,
  );

  useEffect(() => {
    if (!enabled || !tenantId) return;
    if (!data) return;
    if (queryError) return;

    const result = data.cnutGetJobLogPresignedUrl;

    // If the result shape is not what we expect, we don't fetch logs here.
    // We'll derive an error from `data` outside the effect.
    if (!result || result.__typename !== "GetJobLogPresignedUrlOutput") {
      return;
    }

    const url = result.url;
    let cancelled = false;

    const fetchLogs = async () => {
      try {
        setLogsLoading(true);

        const response = await fetch(url, {
          headers: { "Content-Encoding": "gzip" },
        });

        const buf = await response.arrayBuffer();
        const blob = await decompressBlob(new Blob([buf]));
        const text = await blob.text();

        if (cancelled) return;

        const parsedLogs = text
          .split("\n")
          .filter((l) => l.trim().length > 0)
          .map((l) => JSON.parse(l) as LogMessage);

        setLogs(parsedLogs);
        setLogsLoading(false);
        setLogsError(null);
      } catch (e) {
        if (cancelled) return;
        setLogsError(e as Error);
        setLogsLoading(false);
      }
    };

    void fetchLogs();

    return () => {
      cancelled = true;
    };
  }, [enabled, tenantId, data, queryError]);

  // Derive an error for the "invalid typename / result" case
  const invalidResultError =
    enabled &&
    !queryError &&
    data &&
    (!data.cnutGetJobLogPresignedUrl || data.cnutGetJobLogPresignedUrl.__typename !== "GetJobLogPresignedUrlOutput")
      ? new Error(`Failed get presigned url: ${JSON.stringify(data.cnutGetJobLogPresignedUrl)}`)
      : null;

  const combinedError = queryError ?? invalidResultError ?? logsError;
  const combinedLoading = enabled && (queryLoading || logsLoading);

  if (!enabled || !tenantId) {
    return { loading: false, error: null, data: [], logSource };
  }

  if (combinedError) {
    return { loading: false, error: combinedError, data: [], logSource };
  }

  if (combinedLoading) {
    return { loading: true, error: null, data: [], logSource };
  }

  return {
    loading: false,
    error: null,
    data: logs,
    logSource,
  };
};
